import axios from 'axios'
import Cookies from 'js-cookie'
import { ElMessageBox } from 'element-plus'

const API_URL = process.env.VUE_APP_API_URL || 'https://www.echosphere.ltd'

// 创建axios实例
const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// 请求拦截器
api.interceptors.request.use(
  config => {
    const token = Cookies.get('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 响应拦截器
api.interceptors.response.use(
  response => response.data,
  async error => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        const refreshToken = Cookies.get('refresh_token')
        if (refreshToken) {
          const response = await axios.post(`${API_URL}/work/auth/refresh`, {}, {
            headers: {
              'Authorization': `Bearer ${refreshToken}`
            }
          })

          const { access_token } = response.data
          Cookies.set('access_token', access_token)
          originalRequest.headers.Authorization = `Bearer ${access_token}`

          return api(originalRequest)
        } else {
          // 没有refresh token，直接跳转登录
          await handleUnauthorized()
        }
      } catch (refreshError) {
        // 刷新令牌失败，跳转到登录页
        await handleUnauthorized()
      }
    } else if (error.response?.status === 401) {
      // 已经是重试过还是401，直接跳转
      await handleUnauthorized()
    }

    return Promise.reject(error)
  }
)

// 处理未授权的统一函数
async function handleUnauthorized() {
  // 清除认证信息
  Cookies.remove('access_token')
  Cookies.remove('refresh_token')

  // 弹出提示框
  try {
    await ElMessageBox.confirm(
      '登录过期或未登录，请重新登录',
      '提示',
      {
        confirmButtonText: '确定',
        cancelButtonText: false,
        type: 'warning',
        showClose: false,
        closeOnClickModal: false,
        closeOnPressEscape: false
      }
    )
  } catch (error) {
    // 用户点击确定
  }

  // 跳转到登录页
  window.location.href = '/login'
}

// 登录
export const login = (username, password) => {
  return api.post('/work/auth/login', { username, password })
}

// 登出
export const logout = () => {
  return api.post('/work/auth/logout')
}

// 刷新令牌
export const refresh = (refreshToken) => {
  return api.post('/work/auth/refresh', {refresh_token: refreshToken})
}

// ==================== 两步验证（2FA） ====================

// 获取当前两步验证凭证可用的验证方式（登录/忘记密码流程）
export const getTwoFactorMethods = (mfaTicket) => {
  return api.post('/work/auth/2fa/methods', { mfa_ticket: mfaTicket })
}

// 发送邮箱验证码
// 登录/忘记密码流程：携带 mfa_ticket；开启/关闭两步验证流程：携带登录态（不传参）
export const sendTwoFactorEmail = (mfaTicket = null) => {
  const data = mfaTicket ? { mfa_ticket: mfaTicket } : {}
  return api.post('/work/auth/2fa/email/send', data)
}

// 校验验证码/动态码并完成登录
export const verifyTwoFactor = (data) => {
  return api.post('/work/auth/2fa/verify', data)
}

// 查询当前账号两步验证状态（需登录）
export const getTwoFactorStatus = () => {
  return api.get('/work/auth/2fa/status')
}

// 开启两步验证（需登录）
export const enableTwoFactor = () => {
  return api.post('/work/auth/2fa/enable')
}

// 关闭两步验证（需登录，需验证身份：email/authenticator/password）
export const disableTwoFactor = (data) => {
  return api.post('/work/auth/2fa/disable', data)
}

// 开始绑定验证器（返回密钥+二维码）
export const bindAuthenticator = () => {
  return api.post('/work/auth/2fa/authenticator/bind')
}

// 确认绑定验证器（校验动态码）
export const confirmBindAuthenticator = (code) => {
  return api.post('/work/auth/2fa/authenticator/confirm', { code })
}

// 解绑验证器（校验动态码）
export const unbindAuthenticator = (code) => {
  return api.post('/work/auth/2fa/authenticator/unbind', { code })
}

// ==================== 忘记密码 / 重置密码 ====================

// 忘记密码第一步：返回可用验证方式与临时凭证
export const forgotPassword = (username) => {
  return api.post('/work/auth/password/forgot', { username })
}

// 忘记密码第二步：校验验证码/动态码，返回重置密码凭证
export const verifyResetCode = (data) => {
  return api.post('/work/auth/password/verify-code', data)
}

// 忘记密码第三步：使用重置凭证设置新密码
export const resetPasswordByVerification = (data) => {
  return api.post('/work/auth/password/reset', data)
}

// 获取用户信息
export const getProfile = () => {
  return api.get('/work/auth/profile')
}

// 检查权限
export const checkPermission = (permission_code) => {
  return api.post('/work/auth/check-permission', { permission_code })
}

// 获取所有权限（用于用户管理）
export const getAllPermissions = () => {
  return api.get('/work/auth/all-permissions')
}

export default api